import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useRealtime } from '../../contexts/RealtimeContext';
import { getDoc, doc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { signalingService } from '../../firebase/realtimeSignaling';
import { useWebRTC } from '../../hooks/useWebRTC';
import toast from 'react-hot-toast';
import { Loader } from 'lucide-react';
import ParticipantList from '../../components/liveclass/ParticipantList';
import LiveClassChat from '../../components/liveclass/LiveClassChat';
import VideoControls from '../../components/liveclass/VideoControls';
import VideoGrid from '../../components/liveclass/VideoGrid';
import DebugConsole from '../../components/liveclass/DebugConsole';
import PollDisplay from '../../components/liveclass/PollDisplay';
import HostControls from '../../components/liveclass/HostControls';

const LiveClassView = () => {
  const { classId } = useParams();
  const navigate = useNavigate();
  const { currentUser, userProfile } = useRealtime();
  const [loading, setLoading] = useState(true);
  const [liveClass, setLiveClass] = useState(null);
  const [isHost, setIsHost] = useState(false);
  const [error, setError] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [isMuted, setIsMuted] = useState(false);
  const [isVideoOff, setIsVideoOff] = useState(false);
  const [isScreenSharing, setIsScreenSharing] = useState(false);
  const [showChat, setShowChat] = useState(false);
  const [showParticipants, setShowParticipants] = useState(false);
  const [isJoined, setIsJoined] = useState(false);
  const [localStream, setLocalStream] = useState(null);
  const [chatMode, setChatMode] = useState('everyone');
  const screenShareStreamRef = useRef(null);
  const cameraStreamRef = useRef(null);
  
  // Media streams
  const localVideoRef = useRef(null);
  const localStreamRef = useRef(null);
  const participantsUnsubscribeRef = useRef(null);
  
  // Use WebRTC hook to manage peer connections
  const { remoteStreams, peersRef } = useWebRTC(
    classId,
    currentUser?.uid,
    localStream,
    participants
  );

  useEffect(() => {
    const loadLiveClass = async () => {
      if (!classId || !currentUser) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const classDoc = await getDoc(doc(db, 'liveClasses', classId));
        
        if (!classDoc.exists()) {
          setError('Live class not found');
          setLoading(false);
          return;
        }

        const classData = classDoc.data();
        setLiveClass({ id: classDoc.id, ...classData });

        // Check if user is host (instructor)
        const isUserHost = classData.instructorId === currentUser.uid || 
                          classData.hostId === currentUser.uid ||
                          currentUser.uid === classData.createdBy;
        setIsHost(isUserHost);

        setLoading(false);
      } catch (error) {
        console.error('Error loading live class:', error);
        setError('Failed to load live class');
        setLoading(false);
      }
    };

    loadLiveClass();
  }, [classId, currentUser]);

  // Subscribe to participants - set up immediately and keep it active
  useEffect(() => {
    if (!classId || !currentUser || loading) return;
    
    // Don't set up subscription if already subscribed
    if (participantsUnsubscribeRef.current) {
      console.log('Participants subscription already active, skipping setup');
      return;
    }

    console.log('Setting up participants subscription for class:', classId);
    
    // Set up subscription to participants (this will fire immediately and on every change)
    participantsUnsubscribeRef.current = signalingService.subscribeToParticipants(
      classId,
      (participantsList) => {
        console.log('📊 Participants callback fired with', participantsList.length, 'participants');
        console.log('📊 Participant IDs:', participantsList.map(p => p.userId || p.id));
        // Always update state - React will handle re-renders efficiently
        setParticipants(participantsList);
      }
    );

    return () => {
      console.log('Cleaning up participants subscription');
      if (participantsUnsubscribeRef.current) {
        participantsUnsubscribeRef.current();
        participantsUnsubscribeRef.current = null;
      }
    };
  }, [classId, currentUser, loading]);

  // Join room and initialize media
  useEffect(() => {
    if (!classId || !currentUser || !liveClass || loading || isJoined) return;

    const joinClass = async () => {
      try {
        const userName = userProfile?.name || currentUser.displayName || currentUser.email || 'Anonymous';
        
        console.log('Joining room:', classId, 'as', userName, 'isHost:', isHost);
        
        // Join the room
        await signalingService.joinRoom(classId, currentUser.uid, userName, isHost);
        setIsJoined(true);
        toast.success('Joined live class');

        // Initialize local media stream (non-blocking - can still receive even if this fails)
        initializeMedia().catch(err => {
          console.log('Media initialization failed, but continuing to join for receive-only mode:', err);
        });
      } catch (error) {
        console.error('Error joining class:', error);
        toast.error(error.message || 'Failed to join class');
      }
    };

    joinClass();

    return () => {
      // Cleanup on unmount
      if (isJoined) {
        leaveClass();
      }
    };
  }, [classId, currentUser, liveClass, isHost, loading, isJoined]);

  // Cleanup on page unload (browser close/refresh) - best effort
  useEffect(() => {
    const handleBeforeUnload = () => {
      // Try to clean up when user closes tab or refreshes (best effort)
      if (isJoined && classId && currentUser?.uid) {
        // Stop local media tracks immediately
        if (localStreamRef.current) {
          localStreamRef.current.getTracks().forEach(track => track.stop());
        }
        
        // Try to leave room (fire and forget - may not complete)
        signalingService.leaveRoom(classId, currentUser.uid).catch(() => {
          // Ignore errors during unload - cleanup will happen via Firebase rules or other mechanisms
        });
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [classId, currentUser, isJoined]);

  // Initialize media stream (camera and microphone)
  const initializeMedia = async () => {
    if (!classId || !currentUser) return;
    
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: true,
        audio: true
      });
      
      localStreamRef.current = stream;
      cameraStreamRef.current = stream; // Store camera stream for later restoration
      setLocalStream(stream);
      
      if (localVideoRef.current) {
        localVideoRef.current.srcObject = stream;
      }

      // Update initial status
      await signalingService.updateParticipantStatus(classId, currentUser.uid, {
        isMuted: false,
        isVideoOff: false
      });
    } catch (error) {
      console.error('Error accessing media devices:', error);
      toast.error('Failed to access camera/microphone. Please check permissions.');
      
      // Still join without media - can receive streams from others
      console.log('Joining in receive-only mode (no camera/mic access)');
      await signalingService.updateParticipantStatus(classId, currentUser.uid, {
        isMuted: true,
        isVideoOff: true
      });
      setIsMuted(true);
      setIsVideoOff(true);
      // Set localStream to null explicitly so WebRTC hook knows we're in receive-only mode
      setLocalStream(null);
    }
  };

  // Toggle microphone
  const handleToggleMic = async () => {
    try {
      const newMutedState = !isMuted;
      
      if (localStreamRef.current) {
        // Enable/disable audio tracks - this automatically updates peer connections
        localStreamRef.current.getAudioTracks().forEach(track => {
          track.enabled = !newMutedState;
        });
      }

      setIsMuted(newMutedState);
      
      await signalingService.updateParticipantStatus(classId, currentUser.uid, {
        isMuted: newMutedState
      });
    } catch (error) {
      console.error('Error toggling microphone:', error);
      toast.error('Failed to toggle microphone');
    }
  };

  // Toggle video
  const handleToggleVideo = async () => {
    try {
      // Don't allow toggling video while screen sharing
      if (isScreenSharing) {
        toast.error('Please stop screen sharing first');
        return;
      }

      const newVideoOffState = !isVideoOff;
      
      if (localStreamRef.current) {
        // Enable/disable video tracks - this automatically updates peer connections
        localStreamRef.current.getVideoTracks().forEach(track => {
          track.enabled = !newVideoOffState;
        });
      }

      setIsVideoOff(newVideoOffState);
      
      await signalingService.updateParticipantStatus(classId, currentUser.uid, {
        isVideoOff: newVideoOffState
      });
    } catch (error) {
      console.error('Error toggling video:', error);
      toast.error('Failed to toggle video');
    }
  };

  // Toggle screen sharing
  const handleToggleScreenShare = async () => {
    try {
      if (isScreenSharing) {
        // Stop screen sharing
        if (screenShareStreamRef.current) {
          screenShareStreamRef.current.getTracks().forEach(track => {
            track.stop();
          });
          screenShareStreamRef.current = null;
        }

        // Restore camera stream if available
        if (cameraStreamRef.current && localStreamRef.current) {
          const cameraVideoTrack = cameraStreamRef.current.getVideoTracks()[0];
          if (cameraVideoTrack) {
            // Get all peer connections
            const peers = Object.values(peersRef.current || {});
            
            // Replace video track in local stream
            const currentVideoTracks = localStreamRef.current.getVideoTracks();
            currentVideoTracks.forEach(track => {
              localStreamRef.current.removeTrack(track);
              track.stop();
            });
            
            localStreamRef.current.addTrack(cameraVideoTrack);

            // Replace track in all peer connections
            peers.forEach(peer => {
              if (peer && peer._pc) {
                const senders = peer._pc.getSenders();
                const videoSender = senders.find(sender => 
                  sender.track && sender.track.kind === 'video'
                );
                if (videoSender && videoSender.replaceTrack) {
                  videoSender.replaceTrack(cameraVideoTrack).catch(err => {
                    console.error('Error replacing track:', err);
                  });
                }
              }
            });
          }
        }

        setIsScreenSharing(false);
        toast.success('Screen sharing stopped');
      } else {
        // Start screen sharing
        if (!localStreamRef.current) {
          toast.error('Please enable your camera first');
          return;
        }

        // Get screen share stream
        const screenStream = await navigator.mediaDevices.getDisplayMedia({
          video: {
            cursor: 'always',
            displaySurface: 'monitor'
          },
          audio: true // Try to get audio from screen share
        });

        screenShareStreamRef.current = screenStream;

        // Store current video track (camera) if not already stored
        const currentVideoTracks = localStreamRef.current.getVideoTracks();
        if (currentVideoTracks.length > 0 && !cameraStreamRef.current) {
          // Create a new stream with the camera track for storage
          cameraStreamRef.current = new MediaStream([currentVideoTracks[0]]);
        }

        // Get screen share video track
        const screenVideoTrack = screenStream.getVideoTracks()[0];
        if (!screenVideoTrack) {
          toast.error('Failed to get screen share video');
          screenStream.getTracks().forEach(track => track.stop());
          return;
        }

        // Handle screen share ending (user clicks stop in browser)
        screenVideoTrack.onended = () => {
          handleToggleScreenShare();
        };

        // Get all peer connections
        const peers = Object.values(peersRef.current || {});

        // Replace video track in local stream
        currentVideoTracks.forEach(track => {
          localStreamRef.current.removeTrack(track);
        });
        
        localStreamRef.current.addTrack(screenVideoTrack);

        // Add screen share audio if available
        const screenAudioTracks = screenStream.getAudioTracks();
        screenAudioTracks.forEach(track => {
          // Add audio track if not already present
          const existingAudio = localStreamRef.current.getAudioTracks();
          if (existingAudio.length === 0) {
            localStreamRef.current.addTrack(track);
          }
        });

        // Replace video track in all peer connections
        peers.forEach(peer => {
          if (peer && peer._pc) {
            const senders = peer._pc.getSenders();
            const videoSender = senders.find(sender => 
              sender.track && sender.track.kind === 'video'
            );
            if (videoSender && videoSender.replaceTrack) {
              videoSender.replaceTrack(screenVideoTrack).catch(err => {
                console.error('Error replacing track with screen share:', err);
              });
            }
          }
        });

        setIsScreenSharing(true);
        setIsVideoOff(false); // Video is now on (screen share)
        toast.success('Screen sharing started');

        // Update participant status
        await signalingService.updateParticipantStatus(classId, currentUser.uid, {
          isVideoOff: false
        });
      }
    } catch (error) {
      console.error('Error toggling screen share:', error);
      if (error.name === 'NotAllowedError') {
        toast.error('Screen sharing permission denied');
      } else {
        toast.error('Failed to toggle screen sharing');
      }
      setIsScreenSharing(false);
    }
  };

  // Cleanup screen share on unmount
  useEffect(() => {
    return () => {
      if (screenShareStreamRef.current) {
        screenShareStreamRef.current.getTracks().forEach(track => track.stop());
        screenShareStreamRef.current = null;
      }
    };
  }, []);

  // Leave class - comprehensive cleanup
  const leaveClass = async () => {
    try {
      console.log('[LiveClassView] Starting comprehensive cleanup for user:', currentUser?.uid);
      
      // Stop screen sharing if active
      if (screenShareStreamRef.current) {
        screenShareStreamRef.current.getTracks().forEach(track => track.stop());
        screenShareStreamRef.current = null;
        setIsScreenSharing(false);
      }
      
      // Stop media streams
      if (localStreamRef.current) {
        localStreamRef.current.getTracks().forEach(track => {
          track.stop();
          console.log('[LiveClassView] Stopped local track:', track.kind);
        });
        localStreamRef.current = null;
        setLocalStream(null);
      }
      
      // Clear local video element
      if (localVideoRef.current) {
        localVideoRef.current.srcObject = null;
      }

      // Unsubscribe from participants
      if (participantsUnsubscribeRef.current) {
        participantsUnsubscribeRef.current();
        participantsUnsubscribeRef.current = null;
      }

      // Leave room (removes participant data from Firebase)
      if (isJoined && classId && currentUser?.uid) {
        await signalingService.leaveRoom(classId, currentUser.uid);
        console.log('[LiveClassView] Left room and removed participant data');
      }
      
      // Reset state
      setIsJoined(false);
      setParticipants([]);
      
      console.log('[LiveClassView] ✅ Complete cleanup finished');
    } catch (error) {
      console.error('[LiveClassView] Error leaving class:', error);
    }
  };

  const handleLeave = async () => {
    await leaveClass();
    navigate('/dashboard/live-classes');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-900">
        <div className="text-center">
          <Loader className="w-12 h-12 text-blue-500 animate-spin mx-auto mb-4" />
          <p className="text-white text-lg">Loading live class...</p>
        </div>
      </div>
    );
  }

  if (error || !liveClass) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-900">
        <div className="text-center">
          <p className="text-red-500 text-lg mb-4">{error || 'Live class not found'}</p>
          <button
            onClick={() => navigate('/dashboard/live-classes')}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  // Check if class is live or can be joined
  const now = new Date();
  const scheduledAt = liveClass.scheduledAt ? new Date(liveClass.scheduledAt) : null;
  const duration = liveClass.duration || 60;
  const endTime = scheduledAt ? new Date(scheduledAt.getTime() + duration * 60000) : null;
  
  const canJoin = liveClass.status === 'live' || 
    (liveClass.status === 'scheduled' && scheduledAt && 
     now >= new Date(scheduledAt.getTime() - 5 * 60000) && 
     now <= endTime);

  if (!canJoin && !isHost) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-900">
        <div className="text-center bg-gray-800 p-8 rounded-lg">
          <p className="text-white text-lg mb-4">
            {liveClass.status === 'scheduled' 
              ? 'This class has not started yet.'
              : 'This class is not available.'}
          </p>
          {scheduledAt && (
            <p className="text-gray-400 mb-4">
              Scheduled for: {scheduledAt.toLocaleString()}
            </p>
          )}
          <button
            onClick={() => navigate('/dashboard/live-classes')}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen bg-gray-900 overflow-hidden">
      {/* Header */}
      <div className="bg-gray-800 border-b border-gray-700 px-6 py-4 flex items-center justify-between">
        <div>
          <h1 className="text-white text-xl font-semibold">{liveClass.title || 'Live Class'}</h1>
          <p className="text-gray-400 text-sm">
            {participants.length} participant{participants.length !== 1 ? 's' : ''}
          </p>
        </div>
        <div className="flex items-center space-x-4">
          {isHost && (
            <>
              <span className="px-3 py-1 bg-yellow-500/20 text-yellow-400 text-sm rounded-full">
                Host
              </span>
              <HostControls
                classId={classId}
                participants={participants}
                currentUserId={currentUser?.uid}
                chatMode={chatMode}
                onChatModeChange={setChatMode}
              />
            </>
          )}
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 flex overflow-hidden relative">
        {/* Participant List Sidebar */}
        {showParticipants && (
          <ParticipantList
            participants={participants}
            isOpen={showParticipants}
            onClose={() => setShowParticipants(false)}
          />
        )}

        {/* Video Area */}
        <div className="flex-1 flex items-center justify-center bg-gray-900 relative overflow-auto">
          {participants.length > 0 ? (
            <VideoGrid
              participants={participants}
              localStream={localStream}
              remoteStreams={remoteStreams}
              currentUserId={currentUser?.uid}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <div className="text-center">
                <div className="w-24 h-24 rounded-full bg-gray-700 flex items-center justify-center mx-auto mb-4">
                  <span className="text-3xl text-gray-400">
                    {(userProfile?.name || currentUser?.displayName || currentUser?.email || 'U')[0].toUpperCase()}
                  </span>
                </div>
                <p className="text-gray-400">
                  Waiting for participants to join...
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Chat Sidebar */}
        {showChat && (
          <LiveClassChat
            classId={classId}
            isOpen={showChat}
            onClose={() => setShowChat(false)}
            chatMode={chatMode}
            isHost={isHost}
          />
        )}
      </div>

      {/* Video Controls */}
      <VideoControls
        isMuted={isMuted}
        isVideoOff={isVideoOff}
        isScreenSharing={isScreenSharing}
        onToggleMic={handleToggleMic}
        onToggleVideo={handleToggleVideo}
        onToggleScreenShare={handleToggleScreenShare}
        onLeave={handleLeave}
        onToggleChat={() => setShowChat(!showChat)}
        onToggleParticipants={() => setShowParticipants(!showParticipants)}
        showChat={showChat}
        showParticipants={showParticipants}
      />

      {/* Poll Display */}
      <PollDisplay classId={classId} isHost={isHost} />

      {/* Debug Console */}
      <DebugConsole />
    </div>
  );
};

export default LiveClassView;
